import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bookingFlowStore } from '../store/BookingFlowStore';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Button,
    Text, Flex, Box, IconButton, Step, Stack, StepIcon, StepIndicator,
    StepSeparator, StepStatus, StepNumber, StepTitle,
    StepDescription, Stepper, Avatar, useBreakpointValue, Progress,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { appointmentTypesStore } from '../store/AppointmentTypesStore';
import queryString from 'query-string';
import { useBookAppointmentStore } from '../store';
import { useServiceTypesQuery, useAppointmentTypesQuery, useAppointmentSlotsQuery, useUserQuery } from '../graphql/types-and-hooks';
import { doctorsStore } from '../store/DoctorsStore';
import { DateTime } from "luxon";
import BookingSuccess from './BookingSuccess';
import ConfirmationStep from './ConfirmationStep';
import { FaCircleCheck } from "react-icons/fa6";
import { patientStore } from '../store/PatientStore';
import Step1UserSelection from './BookingSteps/Step1_UserSelection';
import Step2ExistingPatient from './BookingSteps/Step2_ExistingPatient';
import Step3ServiceType from './BookingSteps/Step3_ServiceType';
import Step4AppointmentType from './BookingSteps/Step4_AppointmentType';
import Step5Information from './BookingSteps/Step5_Information';
import Step6DoctorSelection from './BookingSteps/Step6_DoctorSelection';
import Step7TimeSelection from './BookingSteps/Step7_TimeSelection';
import Step8Login from './BookingSteps/Step8_Login';
import Step9FamilyMember from './BookingSteps/Step9_FamilyMember';
import { servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import logger from '../utils/logger';
import { logError } from "../../src/components/logger";

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// eslint-disable-next-line
interface LoginStepProps {
    email: string;
    password: string;
    setEmail: (value: string) => void;
    setPassword: (value: string) => void;
    handleLogin: () => void;
    isLoggingIn: boolean;
    loginError: string;
    authView: 'choice' | 'login' | 'register';
    setAuthView: (view: 'choice' | 'login' | 'register') => void;
}


// Access environment variables
const USER_FK = parsed.cid;
const CLINIC_GROUP_CRM = parsed.gid;
const LOCATION_ID = parsed.lid;

// eslint-disable-next-line
interface BookingFlowModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const steps = [
    { label: 'For You / Family' },
    { label: 'Existing Patient' },
    { label: 'Service Type' },
    { label: 'Appointment Type' },
    { label: 'Information' },
    { label: 'Select Doctor' },
    { label: 'Time' },
    { label: 'Login' },
    { label: 'Confirmation' },
];



const BookingFlowModal: React.FC<{ isOpen: boolean, onClose: () => void }> = observer(({ isOpen, onClose }) => {
    const isBaseView = useBreakpointValue({ base: true, lg: false });

    useEffect(() => {
        if (isOpen) {
            bookingFlowStore.setActiveStep(0);
        }
    }, [isOpen]);

    // eslint-disable-next-line
    const [appointmentDetails, setAppointmentDetails] = useState<any>(null);
    const [startDate, setStartDate] = useState<DateTime>(DateTime.now());
    const numberOfColumns = useBreakpointValue({ base: 2, md: 3, lg: 7 });

    const { data: userData, refetch: refetchUserData } = useUserQuery({ variables: {} });
    const user = userData?.user;
    const isLoggedIn = !!user;

    const [patient, setData] = useBookAppointmentStore((state) => [state.patient, state.setData]);

    let isNewPatient = "1";
    if (patient) {
        isNewPatient = patient.is_new_pt === "1" ? "1" : undefined
    } else {
        isNewPatient = bookingFlowStore.appointmentData.isExistingPatient ? undefined : "1"
    }


    const { data: serviceTypesData, loading: serviceTypesLoading, error: serviceTypesError } = useServiceTypesQuery({
        variables: {
            input: {
                clinic_group_crm: CLINIC_GROUP_CRM,
                user_fk: USER_FK,
                location_id: LOCATION_ID,
            },
            appointmentTypesFilters: {
                new_patient: isNewPatient,
            }
        },
    });

    const selectedDoctor = bookingFlowStore.appointmentData.selectedDoctor;

    useEffect(() => {
        if (selectedDoctor?.doctor_id) {
            const firstAvailable = doctorsStore?.selectedAptTypeFirstAvailableTimes?.[selectedDoctor?.doctor_id]?.date;

            if (firstAvailable) {
                setStartDate(DateTime.fromISO(firstAvailable));
            } else {
                setStartDate(DateTime.now());
            }
        }
    }, [selectedDoctor]);


    const [selectedServiceType, setSelectedServiceType] = useState(null);
    const serviceTypes = useMemo(() => serviceTypesData?.serviceTypes, [serviceTypesData]);

  

    const { data: appointmentTypesData, loading: appointmentTypesLoading, error: appointmentTypesError } = useAppointmentTypesQuery({
        variables: {
            input: {
                clinic_group_crm: CLINIC_GROUP_CRM,
                user_fk: USER_FK,
                location_id: LOCATION_ID,
                service_type_id: selectedServiceType?.id,
            },
            filters: {
                new_patient: isNewPatient,
            }
        },
    });

    const appointmentTypes = useMemo(() => appointmentTypesData?.appointmentTypes, [appointmentTypesData]);

    const [appointmentSlotsLoading, setAppointmentSlotsLoading] = useState(false);

    // eslint-disable-next-line
    const { data: appointmentSlotsData, refetch } = useAppointmentSlotsQuery({
        variables: {
            input: {
                clinic_group_crm: CLINIC_GROUP_CRM,
                user_fk: USER_FK,
                location_id: LOCATION_ID,
                doctor_id: bookingFlowStore?.appointmentData?.selectedDoctor?.doctor_id,
                appointment_type_id: appointmentTypesStore.selectedAptType?.appointment_type_id,
                start_date: startDate,
                end_date: startDate.plus({ days: numberOfColumns - 1 }),
            },
        },
        skip: !bookingFlowStore.appointmentData.selectedDoctor?.doctor_id || !appointmentTypesStore.selectedAptType?.appointment_type_id,
    });

    // Track loading manually
    useEffect(() => {
        if (!appointmentSlotsData) {
            setAppointmentSlotsLoading(true);
        } else {
            setAppointmentSlotsLoading(false);
        }
    }, [appointmentSlotsData]);

    const handleButtonClick = (serviceType) => {
        setSelectedServiceType(serviceType);
        bookingFlowStore.updateAppointmentData("serviceType", serviceType);
        bookingFlowStore.nextStep();
        bookingFlowStore.setActiveStep(3);
    };

    useEffect(() => {
        if (isLoggedIn) {
            refetchUserData();
        }
    }, [isLoggedIn, refetchUserData]);

    const handleSelectFamilyMember = (member) => {
        bookingFlowStore.updateAppointmentData("selectedFamilyMember", member);
        servicebookingFlowStore.updateAppointmentData("selectedFamilyMember", member);
        logger.log("Selected family member:", member);

        if (bookingFlowStore.currentStep === 9) {
            bookingFlowStore.goToStep(10);
            bookingFlowStore.setActiveStep(10);
        } else {
            bookingFlowStore.nextStep();
            bookingFlowStore.setActiveStep(bookingFlowStore.currentStep + 1);
            console.log("Navigating to Step 3");
            servicebookingFlowStore.goToStep(3);
            servicebookingFlowStore.setActiveStep(servicebookingFlowStore.currentStep + 1);
        }
    };

    const handleAppointmentClick = (appointmentType) => {
        if (appointmentTypesStore.selectedAptType !== appointmentType) {
            appointmentTypesStore.setSelectedAptType(appointmentType);
        }
        bookingFlowStore.updateAppointmentData("appointmentType", appointmentType);
        bookingFlowStore.nextStep();
        bookingFlowStore.setActiveStep(4);
    };

    const handleCloseModal = () => {
        bookingFlowStore.resetFlow();
        bookingFlowStore.setActiveStep(1);
        setData({ doctor: null });
        onClose();
    };

    const getStepDescription = (index: number) => {

        switch (index) {
            case 0:
                return bookingFlowStore.appointmentData.isForUser === null
                    ? 'Not selected'
                    : bookingFlowStore.appointmentData.isForUser
                        ? 'User'
                        : 'Family Member';
            case 1:
                if (patient?.is_new_pt === undefined) return bookingFlowStore.appointmentData.isExistingPatient ? 'Yes' : 'No';
                return patient.is_new_pt === "0" ? 'Yes' : 'No';
            case 2:
                return bookingFlowStore.appointmentData.serviceType?.name || 'Not selected';
            case 3:
                return bookingFlowStore.appointmentData.appointmentType?.display_name || 'Not selected';
            case 4:
                return '';
            case 5:
                return bookingFlowStore.appointmentData.selectedDoctor?.display_name || 'Not selected';
            case 6:
                return bookingFlowStore.appointmentData.selectedTime?.formattedTime || 'Not selected';
            case 7:
                return user ? `${user.first_name} ${user.last_name}` : 'Not logged in'
            default:
                return '';
        }
    };

    const selectMe = useCallback(async () => {
        try {
            if (!userData?.user) {
                return;
            }

            const currentMainLoginPatient = patientStore.currentMainLoginPatient || {};
            const userp = {
                id: currentMainLoginPatient.id,
                type: "myself",
                first_name: userData.user.first_name,
                last_name: userData.user.last_name,
                dob: userData.user.dob,
                contact_number: userData.user.phone_number,
                is_new_pt: currentMainLoginPatient.is_new_pt ?? '0' ? '0' : '1',
                last_seen_did: currentMainLoginPatient.last_seen_did ?? null,
            };
            patientStore.setCurrentPatient(userp);
            setData({
                patient: userp,
            });
            // console.log("patient", patient);
            bookingFlowStore.setActiveStep(2);
            bookingFlowStore.nextStep();

        } catch (error) {
            logError(error, "Error in selectMe function");
            console.error(`Error fetching patient data: `, error);
        }
        // eslint-disable-next-line
    }, [userData, setData]);

    const goToNextStep = () => {
        bookingFlowStore.nextStep();
        bookingFlowStore.setActiveStep(bookingFlowStore.currentStep);
    };

    const isForFamilyMember = !bookingFlowStore.appointmentData.isForUser;
    const familyMember = bookingFlowStore.appointmentData.selectedFamilyMember;
    const isFamilyMemberExistingPatient = familyMember?.is_new_pt === "0";

    useEffect(() => {
        if (isLoggedIn && bookingFlowStore.currentStep === 2) {
            const isExistingPatient = patient?.is_new_pt === "0";
            bookingFlowStore.updateAppointmentData("isExistingPatient", isExistingPatient);
            bookingFlowStore.setActiveStep(2);
            goToNextStep();
        }
        // eslint-disable-next-line
    }, [isLoggedIn, patient, bookingFlowStore.currentStep]);

    useEffect(() => {
        if (isLoggedIn && bookingFlowStore.currentStep === 8) {

            if (isForFamilyMember && !familyMember) {
                // console.log("Navigating to Family Member Selection (Step 9)");
                bookingFlowStore.goToStep(9);
                bookingFlowStore.setActiveStep(9);
            } else {
                bookingFlowStore.goToStep(10);
                bookingFlowStore.setActiveStep(10);
            }
        }
        // eslint-disable-next-line
    }, [isLoggedIn, bookingFlowStore.currentStep, isForFamilyMember, familyMember]);

    useEffect(() => {
        if (bookingFlowStore.currentStep === 9 && isForFamilyMember && familyMember) {
            bookingFlowStore.updateAppointmentData("isExistingPatient", isFamilyMemberExistingPatient);
            bookingFlowStore.goToStep(10);
            bookingFlowStore.setActiveStep(10);
        }
        // eslint-disable-next-line
    }, [bookingFlowStore.currentStep, isForFamilyMember, familyMember, isFamilyMemberExistingPatient]);


    const handleOpenCreateFamilyMemberModal = () => {
        bookingFlowStore.nextStep();
        bookingFlowStore.setActiveStep(bookingFlowStore.currentStep + 1);
    };

    const renderStep = () => {
        switch (bookingFlowStore.currentStep) {
            case 1:
                return (
                    <Step1UserSelection
                        selectMe={selectMe}
                        onSelectFamilyMember={handleSelectFamilyMember}
                        onOpenCreateFamilyMemberModal={handleOpenCreateFamilyMemberModal}
                    />
                );
            case 2:
                return <Step2ExistingPatient isForFamilyMember={!bookingFlowStore.appointmentData.isForUser} />;
            case 3:
                return (
                    <Step3ServiceType
                        serviceTypes={serviceTypes}
                        isLoading={serviceTypesLoading}
                        error={serviceTypesError}
                        onSelect={handleButtonClick}
                    />
                );

            case 4:
                return (
                    <Step4AppointmentType
                        appointmentTypes={appointmentTypes}
                        isLoading={appointmentTypesLoading}
                        error={appointmentTypesError}
                        onSelect={handleAppointmentClick}
                        selectedDoctor={selectedDoctor}
                        userFk={USER_FK as string}
                        clinicGroupCrm={CLINIC_GROUP_CRM as string}
                        locationId={LOCATION_ID as string}
                    />
                );
            case 5:
                return <Step5Information />;
            case 6:
                return <Step6DoctorSelection />;
            case 7:
                return (
                    <Step7TimeSelection
                        appointmentSlotsData={appointmentSlotsData}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        numberOfColumns={numberOfColumns}
                        isLoading={appointmentSlotsLoading}
                    />
                );
            case 8:
                return <Step8Login />;
            case 9:
                return (
                    <Step9FamilyMember
                        onSelectFamilyMember={handleSelectFamilyMember}
                        onOpenCreateFamilyMemberModal={handleOpenCreateFamilyMemberModal}
                    />
                );
            default:
                console.log(`Unexpected step: ${bookingFlowStore.currentStep}`);
                return null;
            case 10:

                return bookingFlowStore.isBookingConfirmed
                    ? <BookingSuccess onClose={handleCloseModal} appointmentDetails={appointmentDetails} />
                    : <ConfirmationStep onClose={handleCloseModal} selectedTime={bookingFlowStore.appointmentData.selectedTime} />;

        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleCloseModal} size="full">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader display="flex" justifyContent="space-between" alignItems="center">
                    <Text fontSize="lg" fontWeight="bold">Booking Appointment</Text>
                    <IconButton icon={<CloseIcon />} aria-label="Close modal" onClick={onClose} variant="ghost" />
                </ModalHeader>
                <ModalBody>
                    <Flex>
                        {/* Sidebar Section */}
                        <Box
                            bg="gray.100"
                            width="15%"
                            minWidth="300px"
                            minHeight="85vh"
                            p={6}
                            borderRadius="md"
                            boxShadow="sm"
                            display={{ base: 'none', lg: 'flex' }}
                            flexDirection="column"
                        >
                            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems="center">
                                {isLoggedIn ? (
                                    <Text fontWeight="bold">Hi, {user?.first_name}</Text>
                                ) : (
                                    // Show login button when not logged in
                                    <>
                                        <Text mr={2}>Have an account?</Text>
                                        <Button
                                            variant="outline"
                                            colorScheme="red"
                                            size="sm"
                                            border="2px"
                                            onClick={() => {
                                                bookingFlowStore.goToLogin();
                                                bookingFlowStore.setActiveStep(8);
                                            }}
                                            _hover={{
                                                bg: "#B92F2F",
                                                color: "#fff",
                                            }}
                                        >
                                            Login
                                        </Button>
                                    </>
                                )}
                            </Box>




                            <Stack mt={5}>
                                <Stepper size="sm" index={bookingFlowStore.activeStep} orientation="vertical" height="400px" gap="0">
                                    {steps.map((step, index) => {
                                        const stepDescription = getStepDescription(index);
                                        const isCompletedStep = !!stepDescription && stepDescription !== "Not selected" && stepDescription !== "Not logged in" && stepDescription !== "No";

                                        return (
                                            <Step key={index} >
                                                <StepIndicator>
                                                    <StepStatus
                                                        complete={<StepIcon />}
                                                        incomplete={isCompletedStep ? <FaCircleCheck size={'md'} color='#3182ce' /> : <StepNumber />}
                                                    />
                                                </StepIndicator>

                                                <Box flexShrink="0" maxWidth="230px">
                                                    <StepTitle>{step.label}</StepTitle>
                                                    <StepDescription>{stepDescription}</StepDescription>
                                                </Box>
                                                <StepSeparator />
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                            </Stack>




                            {bookingFlowStore.appointmentData.selectedDoctor && (
                                <Box display="flex" flexDirection="row" alignItems="center" mt={5}>
                                    <Avatar
                                        name={bookingFlowStore.appointmentData.selectedDoctor.display_name}
                                        size="md"
                                        src={bookingFlowStore.appointmentData.selectedDoctor.image}
                                    />
                                    <Box display="flex" flexDirection="column" ml={3}>
                                        <Text fontWeight="bold">
                                            {bookingFlowStore.appointmentData.selectedDoctor.display_name}
                                        </Text>
                                        <Text fontSize="sm" color="gray.600">
                                            {bookingFlowStore.appointmentData.selectedTime?.date || " "} {bookingFlowStore.appointmentData.selectedTime?.formattedTime || " "}
                                        </Text>
                                    </Box>
                                </Box>
                            )}

                            {/* User Details */}
                            {isLoggedIn && (
                                <Box bg="white" p={3} mt={4} borderRadius="md" boxShadow="sm">
                                    <Text fontWeight="bold">Patient Details</Text>
                                    <Text fontSize="sm"><b>Name:</b> {patientStore.currentPatient?.first_name || 'N/A'} {patient?.last_name || 'N/A'}</Text>
                                    <Text fontSize="sm"><b>Date of Birth:</b> {patientStore.currentPatient?.dob}</Text>

                                </Box>
                            )}

                            <Box flexGrow={1} mt={5} />
                        </Box>

                        {/* Main Content Section */}
                        <Box flex="1" display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">
                            {/* Progress Bar */}
                            <Box width="100%" mb={4} display={{ base: 'block', md: 'none' }}>
                                <Progress
                                    value={(bookingFlowStore.currentStep / steps.length) * 100}
                                    size="sm"
                                    sx={{
                                        '& > div': { backgroundColor: '#B92F2F' }
                                    }}
                                />
                            </Box>
                            <Box flex="1" pl={6} pr={6} display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%">

                                {/* Render Current Step */}
                                {renderStep()}

                            </Box></Box>

                    </Flex>
                </ModalBody>

                {/* {isBaseView && ( */}
                <ModalFooter
                    justifyContent="space-between"
                    bg="white"
                    boxShadow="md"
                    p={5}
                    position="sticky"
                    bottom="0"
                    width="100%"
                >
                    {!(bookingFlowStore.currentStep === 10 && bookingFlowStore.appointmentDetails) ? (
                        <Button
                            onClick={async () => { await bookingFlowStore.handleBackClick() }}
                            variant="outline"
                            borderColor="#B92F2F"
                            color="#B92F2F"
                            border="2px"
                            borderRadius={30}
                            minWidth={'150px'}
                            width={'15%'}
                            _hover={{
                                bg: "#B92F2F",
                                color: "#fff",
                            }}
                        >
                            Back
                        </Button>) : null}
                    {isBaseView && (<Box display={'flex'} flexDirection={'row'}>
                        {isLoggedIn ? (
                            <Text fontWeight="bold">Hi, {user?.first_name}</Text>
                        ) : (
                            // Show login button when not logged in
                            <Box display='flex' flexDirection={'row'}>
                                <Text mr={2}>Have an account?</Text>
                                <Button
                                    variant="outline"
                                    colorScheme="red"
                                    size="sm"
                                    border="2px"
                                    onClick={() => {
                                        bookingFlowStore.goToLogin();
                                        bookingFlowStore.setActiveStep(8);
                                    }}
                                    _hover={{
                                        bg: "#B92F2F",
                                        color: "#fff",
                                    }}
                                >
                                    Login
                                </Button>
                            </Box>
                        )}
                    </Box>)}
                </ModalFooter>
                {/* )} */}
            </ModalContent>
        </Modal>
    );
});

export default BookingFlowModal;